// BNA Portal — composite-priority math. Phase 1.
//
// This is the ENTIRE recalc loop, and it is deliberately pure math: no AI, no network,
// no I/O. The AI's only job (Phase 3) is to produce the cached per-lever `score` values
// fed in here; ranking itself never calls a model, so moving a slider re-ranks instantly
// and identically on the edge (Worker) and in the browser (SPA). Both import this file so
// the two can never disagree.
//
// Axes (all 0..100), per db/seeds/002_levers.sql:
//   lever position : 0 = full LEFT pole, 50 = neutral, 100 = full RIGHT pole
//   bna score      : 0 = action serves LEFT pole, 100 = action serves RIGHT pole
//
// For one lever, alignment is the product of how far the operator pushed the slider off
// neutral and how strongly the action serves that same direction:
//
//   align = ((position - 50) / 50) * ((score - 50) / 50)        // in [-1, +1]
//
// +1  = slider hard toward a pole AND the action fully serves that pole (maximally relevant)
// -1  = slider hard toward a pole but the action serves the OPPOSITE pole (actively off-posture)
//  0  = slider neutral, or the action is neutral on this lever
//
// Composite priority sits around the strategy-independent base_urgency and is nudged
// up or down by the mean weighted alignment across active levers:
//
//   priority = base_urgency + mean_over_levers( weight * align )
//
// With the v0.1 default of three levers at weight 30 (build-plan Decisions #3 and #5),
// the lever term is bounded to [-30, +30] — so W=30 is the maximum a fully-aligned or
// fully-misaligned posture can move an action's priority away from its raw urgency.

export interface LeverState {
  id: string;
  /** Per-lever weight (v0.1 default 30 for all three). */
  weight: number;
  /** Current shared slider position, 0..100. */
  position: number;
}

/** A BNA's cached per-lever alignment scores, keyed by lever id. score is 0..100. */
export type BnaScores = Record<string, number>;

const clamp01to100 = (n: number): number => Math.max(0, Math.min(100, n));

/** Single-lever alignment in [-1, +1]. Exported for unit-level reasoning/tests. */
export function leverAlignment(position: number, score: number): number {
  const p = (clamp01to100(position) - 50) / 50;
  const s = (clamp01to100(score) - 50) / 50;
  return p * s;
}

/**
 * Composite priority for one BNA against the current lever posture.
 * Pure function of cached scores + slider positions — never calls an AI.
 *
 * Levers with no score for this BNA contribute 0 (treated as neutral) but still count
 * toward the mean, so a BNA scored on only some levers isn't artificially inflated.
 */
export function computePriority(
  baseUrgency: number,
  scores: BnaScores,
  levers: LeverState[],
): number {
  if (levers.length === 0) return baseUrgency;
  let weightedSum = 0;
  for (const lever of levers) {
    const score = scores[lever.id];
    if (score === undefined) continue; // neutral contribution
    weightedSum += lever.weight * leverAlignment(lever.position, score);
  }
  return baseUrgency + weightedSum / levers.length;
}

export interface RankableBna {
  id: string;
  baseUrgency: number;
  scores: BnaScores;
}

export interface RankedBna<T extends RankableBna = RankableBna> {
  bna: T;
  priority: number;
}

/**
 * Rank a list of BNAs by composite priority, highest first. Stable on ties by id so the
 * order is deterministic across edge and browser. Returns priority unrounded; round only
 * for display.
 */
export function rankBnas<T extends RankableBna>(bnas: T[], levers: LeverState[]): RankedBna<T>[] {
  return bnas
    .map((bna) => ({ bna, priority: computePriority(bna.baseUrgency, bna.scores, levers) }))
    .sort((a, b) => b.priority - a.priority || a.bna.id.localeCompare(b.bna.id));
}
